import { generateClient } from 'aws-amplify/api'
import {
  listContexts as listContextsQuery,
  listContextCallHistory as listContextCallHistoryQuery,
} from '@/graphql/queries'
import {
  createContext as createContextMutation,
  updateContext as updateContextMutation,
  enableContext as enableContextMutation,
  disableContext as disableContextMutation,
  deleteContext as deleteContextMutation,
  recordContextFile as recordContextFileMutation,
  removeContextFile as removeContextFileMutation,
  editContextFile as editContextFileMutation,
  addContextFields as addContextFieldsMutation,
  removeContextFields as removeContextFieldsMutation,
  updateContextReportColumns as updateContextReportColumnsMutation,
  addContextLeadStatuses as addContextLeadStatusesMutation,
  removeContextLeadStatuses as removeContextLeadStatusesMutation,
} from '@/graphql/mutations'
import { parseAwsJson } from '@/utils/graphql'
import { getApiMessage } from '@/mixins/Extras/ApiResponse'

const client = generateClient()

function normalizeFilename(value) {
  return String(value ?? '').trim().replace(/^.*[\\/]/, '')
}

function buildContextFiles(files = []) {
  const seen = new Set()
  const result = []

  for (const entry of files) {
    const filename =
      typeof entry === 'string'
        ? normalizeFilename(entry)
        : normalizeFilename(entry?.filename ?? entry?.name)

    if (!filename) continue
    const key = filename.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    result.push({ filename })
  }

  return result
}

async function runContextsField(document, field, input) {
  try {
    const variables = input == null ? undefined : { input }
    const { data, errors } = await client.graphql({
      query: document,
      ...(variables ? { variables } : {}),
    })

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    return parseAwsJson(data?.[field])
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Request failed'),
    }
  }
}

export function fetchContexts(input = {}) {
  return runContextsField(listContextsQuery, 'listContexts', {
    org_id: String(input.org_id ?? input.orgId ?? '').trim(),
  })
}

export function createContext(input = {}) {
  const files = buildContextFiles(input.files ?? input.fileList ?? [])
  if (!files.length) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'At least one file is required to create a product.',
    })
  }

  // createContext: org_id + context_name + files (mandatory).
  // action_id / action_name / greeting / mission_prompt are not accepted —
  // the linked OUTGOING_CALL action is auto-created server-side.
  return runContextsField(createContextMutation, 'createContext', {
    org_id: String(input.org_id ?? input.orgId ?? '').trim(),
    context_name: String(input.context_name ?? input.contextName ?? '').trim(),
    files,
  })
}

export function updateContext(input = {}) {
  // updateContext: context_id + context_name always required.
  // files optional (new files only). action_id / action_name not accepted —
  // the action name is auto-derived from context_name server-side.
  // greeting / mission_prompt optional — edit linked action.
  // The unique identifier is managed via addContextFields' is_unique_identifier
  // flag, not here.
  const payload = {
    context_id: String(input.context_id ?? input.contextId ?? '').trim(),
    context_name: String(input.context_name ?? input.contextName ?? '').trim(),
  }

  const files = buildContextFiles(input.files ?? input.fileList ?? [])
  if (files.length) payload.files = files

  const greeting = String(input.greeting ?? '').trim()
  if (greeting) payload.greeting = greeting

  const missionPrompt = String(
    input.mission_prompt ?? input.missionPrompt ?? '',
  ).trim()
  if (missionPrompt) payload.mission_prompt = missionPrompt

  // typeof check so false survives — gates the campaign "Create lead" button.
  const allowManualLeads = input.allow_manual_leads ?? input.allowManualLeads
  if (typeof allowManualLeads === 'boolean') payload.allow_manual_leads = allowManualLeads

  // Product-level default for the platform's per-lead daily dial ceiling --
  // a campaign under this product can still override it. Explicit null (not
  // just omitted) is how "clear it back to the platform fallback" is sent.
  const dailyDialCap = input.daily_dial_cap ?? input.dailyDialCap
  if (dailyDialCap !== undefined) {
    payload.daily_dial_cap = dailyDialCap === null ? null : Number(dailyDialCap)
  }

  appendDashboardDigestFields(payload, input)

  return runContextsField(updateContextMutation, 'updateContext', payload)
}

// Dashboard Digest fields (createContext / updateContext) — periodic KPI +
// agent-activity email/WhatsApp summary, off by default per product. Same
// typeof/Array.isArray "only touch what was sent" shape as allowManualLeads
// above, mirrored from campaignsApi.js's own appendDashboardDigestFields.
function appendDashboardDigestFields(payload, input = {}) {
  const sendByEmail = input.send_dashboard_digest_by_email ?? input.sendDashboardDigestByEmail
  if (typeof sendByEmail === 'boolean') payload.send_dashboard_digest_by_email = sendByEmail

  const emailAddresses = input.dashboard_digest_email_addresses ?? input.dashboardDigestEmailAddresses
  if (Array.isArray(emailAddresses)) payload.dashboard_digest_email_addresses = emailAddresses

  const sendByWhatsapp = input.send_dashboard_digest_by_whatsapp ?? input.sendDashboardDigestByWhatsapp
  if (typeof sendByWhatsapp === 'boolean') payload.send_dashboard_digest_by_whatsapp = sendByWhatsapp

  const whatsappNumbers = input.dashboard_digest_whatsapp_numbers ?? input.dashboardDigestWhatsappNumbers
  if (Array.isArray(whatsappNumbers)) payload.dashboard_digest_whatsapp_numbers = whatsappNumbers

  const ccAgents = input.dashboard_digest_cc_agents ?? input.dashboardDigestCcAgents
  if (typeof ccAgents === 'boolean') payload.dashboard_digest_cc_agents = ccAgents

  const digestTimes = input.dashboard_digest_times ?? input.dashboardDigestTimes
  if (Array.isArray(digestTimes)) payload.dashboard_digest_times = digestTimes

  // Independent weekly/monthly cadences alongside the daily times above.
  const weeklyEnabled = input.dashboard_digest_weekly_enabled ?? input.dashboardDigestWeeklyEnabled
  if (typeof weeklyEnabled === 'boolean') payload.dashboard_digest_weekly_enabled = weeklyEnabled

  const weeklyDay = input.dashboard_digest_weekly_day ?? input.dashboardDigestWeeklyDay
  if (weeklyDay != null) payload.dashboard_digest_weekly_day = weeklyDay

  const weeklyTime = input.dashboard_digest_weekly_time ?? input.dashboardDigestWeeklyTime
  if (weeklyTime != null) payload.dashboard_digest_weekly_time = weeklyTime

  const monthlyEnabled = input.dashboard_digest_monthly_enabled ?? input.dashboardDigestMonthlyEnabled
  if (typeof monthlyEnabled === 'boolean') payload.dashboard_digest_monthly_enabled = monthlyEnabled

  const monthlyDay = input.dashboard_digest_monthly_day ?? input.dashboardDigestMonthlyDay
  if (monthlyDay != null) payload.dashboard_digest_monthly_day = monthlyDay

  const monthlyTime = input.dashboard_digest_monthly_time ?? input.dashboardDigestMonthlyTime
  if (monthlyTime != null) payload.dashboard_digest_monthly_time = monthlyTime
}

// EMAIL was already offered in CONTEXT_FIELD_TYPE_OPTIONS (utils/contexts.js)
// and validated server-side, but missing from this save-time filter --
// meaning picking "Email" as a field type here silently dropped the field
// instead of saving it. TIME added alongside as a genuinely new type.
const CONTEXT_FIELD_TYPES = new Set([
  'TEXT',
  'NUMBER',
  'DROPDOWN',
  'PHONE_NUMBER',
  'DATE',
  'TIME',
  'EMAIL',
])

/**
 * Declare (or overwrite) lead field definitions on a context.
 * field_definitions: [{ label, key?, type: TEXT|NUMBER|DROPDOWN|PHONE_NUMBER|DATE,
 *   options?: [], enabled?: Boolean, is_unique_identifier?: Boolean }]
 * label is mandatory. key is only needed when editing an EXISTING field —
 * pass the map key read from custom_field_definitions. Omit it for new
 * fields and the server derives the key by slugifying the label.
 * This overwrites the named field's entire definition — always resend the
 * full current state of a field (including is_unique_identifier) when
 * editing, or the omitted flags are cleared server-side.
 * Response data.custom_field_definitions is the full updated registry —
 * use it to update local state without a follow-up listContexts call.
 */
export function addContextFields(input = {}) {
  const contextId = String(input.context_id ?? input.contextId ?? '').trim()

  const definitions = (Array.isArray(input.field_definitions)
    ? input.field_definitions
    : []
  )
    .map((definition) => {
      const label = String(definition?.label ?? definition?.name ?? '').trim()
      const key = String(definition?.key ?? '').trim()
      const type = String(definition?.type ?? '').trim().toUpperCase()
      if (!label || !CONTEXT_FIELD_TYPES.has(type)) return null

      const entry = { label, type }
      if (key) entry.key = key
      if (type === 'DROPDOWN') {
        const options = (Array.isArray(definition?.options)
          ? definition.options
          : []
        )
          .map((option) => String(option ?? '').trim())
          .filter(Boolean)
        if (!options.length) return null
        entry.options = options
      }
      if (definition?.enabled != null) entry.enabled = Boolean(definition.enabled)
      if (definition?.is_unique_identifier != null) {
        entry.is_unique_identifier = Boolean(definition.is_unique_identifier)
      }
      return entry
    })
    .filter(Boolean)

  if (!contextId || !definitions.length) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Context id and at least one valid field definition are required.',
    })
  }

  return runContextsField(addContextFieldsMutation, 'addContextFields', {
    context_id: contextId,
    field_definitions: definitions,
  })
}

/**
 * Remove custom lead field definitions from a context.
 * field_names: ["budget_range", ...]
 */
export function removeContextFields(input = {}) {
  const contextId = String(input.context_id ?? input.contextId ?? '').trim()

  const fieldNames = (Array.isArray(input.field_names)
    ? input.field_names
    : [input.field_names]
  )
    .map((name) => String(name ?? '').trim())
    .filter(Boolean)

  if (!contextId || !fieldNames.length) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Context id and at least one field name are required.',
    })
  }

  return runContextsField(removeContextFieldsMutation, 'removeContextFields', {
    context_id: contextId,
    field_names: fieldNames,
  })
}

const REPORT_COLUMN_KINDS = new Set(['FIXED', 'DYNAMIC'])

/**
 * Save one report type's column config (order + visibility) for a product.
 * columns: [{ id, kind: 'FIXED'|'DYNAMIC', enabled }], array order = display order.
 */
export function updateContextReportColumns(input = {}) {
  const contextId = String(input.context_id ?? input.contextId ?? '').trim()
  const reportType = String(input.report_type ?? input.reportType ?? '').trim()

  const columns = (Array.isArray(input.columns) ? input.columns : [])
    .map((c) => ({
      id: String(c?.id ?? '').trim(),
      kind: String(c?.kind ?? '').trim().toUpperCase(),
      enabled: c?.enabled !== false,
    }))
    .filter((c) => c.id && REPORT_COLUMN_KINDS.has(c.kind))

  if (!contextId || !reportType) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Context id and report_type are required.',
    })
  }

  return runContextsField(updateContextReportColumnsMutation, 'updateContextReportColumns', {
    context_id: contextId,
    report_type: reportType,
    columns,
  })
}

function cleanStatusList(value) {
  return (Array.isArray(value) ? value : [value])
    .map((status) => String(status ?? '').trim().toUpperCase().replace(/\s+/g, '_'))
    .filter(Boolean)
}

/**
 * Add status value(s) to this product's own lead_statuses list.
 * Duplicates are silently ignored server-side.
 * Response data: { statuses } — the full updated list.
 */
export function addContextLeadStatuses(input = {}) {
  const contextId = String(input.context_id ?? input.contextId ?? '').trim()
  const statuses = cleanStatusList(input.statuses)

  if (!contextId || !statuses.length) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Context id and at least one status are required.',
    })
  }

  return runContextsField(addContextLeadStatusesMutation, 'addContextLeadStatuses', {
    context_id: contextId,
    statuses,
  })
}

/**
 * Remove status value(s) from this product's lead_statuses list. The server
 * blocks removing the last remaining status or any status still in use by
 * existing leads. Response data: { statuses } — the full updated list.
 */
export function removeContextLeadStatuses(input = {}) {
  const contextId = String(input.context_id ?? input.contextId ?? '').trim()
  const statuses = cleanStatusList(input.statuses)

  if (!contextId || !statuses.length) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Context id and at least one status are required.',
    })
  }

  return runContextsField(
    removeContextLeadStatusesMutation,
    'removeContextLeadStatuses',
    { context_id: contextId, statuses },
  )
}

/**
 * Aggregated AI-dialer call log across every lead in a context, newest
 * first. limit defaults to 50, capped at 500 server-side; no pagination
 * cursor beyond limit. data: { items, total }.
 */
export function fetchContextCallHistory(input = {}) {
  const payload = {
    context_id: String(input.context_id ?? input.contextId ?? '').trim(),
  }
  const limit = Number(input.limit)
  if (Number.isFinite(limit) && limit > 0) payload.limit = limit

  return runContextsField(
    listContextCallHistoryQuery,
    'listContextCallHistory',
    payload,
  )
}

export function enableContext(input = {}) {
  return runContextsField(enableContextMutation, 'enableContext', {
    context_id: String(input.context_id ?? input.contextId ?? '').trim(),
  })
}

export function disableContext(input = {}) {
  return runContextsField(disableContextMutation, 'disableContext', {
    context_id: String(input.context_id ?? input.contextId ?? '').trim(),
  })
}

export function deleteContext(input = {}) {
  return runContextsField(deleteContextMutation, 'deleteContext', {
    context_id: String(input.context_id ?? input.contextId ?? '').trim(),
  })
}

export function recordContextFile(input = {}) {
  return runContextsField(recordContextFileMutation, 'recordContextFile', {
    context_id: String(input.context_id ?? input.contextId ?? '').trim(),
    s3_key: String(input.s3_key ?? input.s3Key ?? '').trim(),
    filename: normalizeFilename(input.filename ?? input.fileName),
  })
}

export function removeContextFile(input = {}) {
  return runContextsField(removeContextFileMutation, 'removeContextFile', {
    context_id: String(input.context_id ?? input.contextId ?? '').trim(),
    s3_key: String(input.s3_key ?? input.s3Key ?? '').trim(),
  })
}

export function editContextFile(input = {}) {
  return runContextsField(editContextFileMutation, 'editContextFile', {
    context_id: String(input.context_id ?? input.contextId ?? '').trim(),
    s3_key: String(input.s3_key ?? input.s3Key ?? '').trim(),
    filename: normalizeFilename(input.filename ?? input.fileName),
  })
}
